import {
    type InflowSegments,
    buildInflowSegments,
    computeInflowCdf,
} from "./ParticleInflow";
import {
    PARTICLE_COLORS,
    PARTICLE_HEIGHT,
    PARTICLE_START_COUNT,
    PARTICLE_STEPS_PER_FT,
    PARTICLE_WIDTH,
    type ParticleField,
    type ParticleGeometry,
    type ParticleRenderer,
    type ParticleSettings,
    particleCount,
    seededRandom,
    warmupWindowSteps,
} from "./ParticleTypes";

type Programs = Record<"advect" | "fade" | "line" | "composite", WebGLProgram>;

export class ParticleGL implements ParticleRenderer {
    readonly kind = "webgl" as const;
    private readonly canvas = document.createElement("canvas");
    private readonly gl: WebGL2RenderingContext;
    private readonly programs: Programs;
    private readonly quadVao: WebGLVertexArrayObject;
    private readonly lineVao: WebGLVertexArrayObject;
    private field!: ParticleField;
    private geometry!: ParticleGeometry;
    private settings!: ParticleSettings;
    private count = PARTICLE_START_COUNT;
    private renderedCount = PARTICLE_START_COUNT - 1;
    private particleN = 0;
    private stateSide = 0;
    private stateIndex = 0;
    private trailIndex = 0;
    private windFt = -1;
    private stateTextures: WebGLTexture[] = [];
    private stateFramebuffers: WebGLFramebuffer[] = [];
    private segmentTexture: WebGLTexture | null = null;
    private windTextures: WebGLTexture[] = [];
    private projectionTexture: WebGLTexture | null = null;
    private validTexture: WebGLTexture | null = null;
    private inflow: InflowSegments | null = null;
    private inflowCdf = new Float32Array();
    private inflowCdfRows = 1;
    private inflowSegTexture: WebGLTexture | null = null;
    private inflowCdfTexture: WebGLTexture | null = null;
    private inflowRate = 0;
    private trailTextures: WebGLTexture[] = [];
    private trailFramebuffers: WebGLFramebuffer[] = [];

    constructor(
        private readonly presentation: HTMLCanvasElement,
        onContextLost: () => void,
        allowSoftware = false,
    ) {
        this.canvas.width = PARTICLE_WIDTH;
        this.canvas.height = PARTICLE_HEIGHT;
        const gl = this.canvas.getContext("webgl2", {
            alpha: true,
            premultipliedAlpha: true,
            antialias: false,
            preserveDrawingBuffer: false,
        });
        if (!gl) {
            throw new Error("WebGL2を利用できません。");
        }
        if (!gl.getExtension("EXT_color_buffer_float")) {
            throw new Error("EXT_color_buffer_floatを利用できません。");
        }
        const debug = gl.getExtension("WEBGL_debug_renderer_info");
        if (debug && !allowSoftware) {
            const renderer = String(
                gl.getParameter(debug.UNMASKED_RENDERER_WEBGL) || "",
            );
            if (/swiftshader|llvmpipe|software/i.test(renderer)) {
                throw new Error(`ソフトウェアWebGLを検出しました: ${renderer}`);
            }
        }
        this.canvas.addEventListener("webglcontextlost", (event) => {
            event.preventDefault();
            onContextLost();
        });
        this.gl = gl;
        this.programs = this.compilePrograms();
        [this.quadVao, this.lineVao] = this.createVaos();
    }

    configure(
        field: ParticleField,
        geometry: ParticleGeometry,
        settings: ParticleSettings,
    ) {
        this.field = field;
        this.geometry = geometry;
        this.settings = settings;
        this.buildFieldTextures();
        this.reset();
    }

    reset() {
        if (!this.field) {
            return;
        }
        this.count = PARTICLE_START_COUNT;
        this.renderedCount = PARTICLE_START_COUNT - 1;
        this.windFt = -1;
        this.stateIndex = 0;
        this.trailIndex = 0;
        this.buildStateTextures();
        this.buildTrailTextures();
        this.clearPresentation();
    }

    renderTo(ft: number) {
        const target = Math.max(
            0,
            Math.round((ft - this.field.startFt) * PARTICLE_STEPS_PER_FT),
        );
        if (target < this.renderedCount) {
            this.reset();
        }
        const window = warmupWindowSteps(this.settings);
        while (this.count <= target) {
            // ウォームアップ (残りが減衰窓より遠い) 中は移流のみ (フェード/線描画を省略)
            this.step(target - this.count < window);
        }
        this.present();
    }

    step(drawTrail = true) {
        const relativeFt = Math.max(this.count / PARTICLE_STEPS_PER_FT, 0);
        const ftA = Math.min(Math.floor(relativeFt), this.field.timeCount - 1);
        if (ftA !== this.windFt) {
            this.uploadWind(0, ftA);
            this.uploadWind(1, Math.min(ftA + 1, this.field.timeCount - 1));
            this.updateInflow(ftA);
            this.windFt = ftA;
        }
        const fraction = Math.min(Math.max(relativeFt - ftA, 0), 1);
        this.advect(fraction);
        if (drawTrail) {
            this.fadeTrail();
            this.drawLines();
        }
        this.renderedCount = this.count;
        this.count++;
        return this.getCurrentFt();
    }

    present() {
        const gl = this.gl;
        const program = this.programs.composite;
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, PARTICLE_WIDTH, PARTICLE_HEIGHT);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.disable(gl.BLEND);
        gl.useProgram(program);
        this.bindTexture(
            program,
            0,
            this.trailTextures[this.trailIndex],
            "uTrail",
        );
        this.bindTexture(program, 1, this.windTextures[0], "uWindA");
        this.bindTexture(program, 2, this.windTextures[1], "uWindB");
        this.bindTexture(program, 3, this.projectionTexture!, "uProjection");
        const relativeFt = Math.max(this.count / PARTICLE_STEPS_PER_FT, 0);
        this.uniform1f(
            program,
            "uFraction",
            Math.min(relativeFt - this.windFt, 1),
        );
        this.uniform2f(
            program,
            "uGridSize",
            this.field.width,
            this.field.height,
        );
        this.uniform2f(program, "uScreen", PARTICLE_WIDTH, PARTICLE_HEIGHT);
        this.uniform2f(
            program,
            "uBack",
            this.geometry.backWidth,
            this.geometry.backHeight,
        );
        this.uniform2f(program, "uOrigin", this.geometry.rx, this.geometry.ry);
        this.uniform2f(
            program,
            "uInvGrid",
            1 / (this.geometry.gridDx * this.geometry.scale),
            1 / (this.geometry.gridDy * this.geometry.scale),
        );
        const thresholds = new Float32Array(6);
        for (let index = 0; index < 6; index++) {
            thresholds[index] = Math.pow(
                (index + 1) * this.settings.colorThreshold,
                2,
            );
        }
        gl.uniform1fv(
            gl.getUniformLocation(program, "uThresholds"),
            thresholds,
        );
        gl.uniform3fv(
            gl.getUniformLocation(program, "uColors"),
            new Float32Array(PARTICLE_COLORS[this.settings.colorMode].flat()),
        );
        gl.uniformMatrix3fv(
            gl.getUniformLocation(program, "uView"),
            false,
            this.viewMatrix(),
        );
        gl.bindVertexArray(this.quadVao);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        const context = this.presentation.getContext("2d")!;
        context.clearRect(0, 0, PARTICLE_WIDTH, PARTICLE_HEIGHT);
        context.drawImage(this.canvas, 0, 0);
    }

    getCurrentFt() {
        return (
            this.field.startFt +
            Math.max(this.renderedCount, 0) / PARTICLE_STEPS_PER_FT
        );
    }

    dispose() {
        const gl = this.gl;
        for (const texture of [
            ...this.stateTextures,
            ...this.windTextures,
            ...this.trailTextures,
            this.segmentTexture,
            this.projectionTexture,
            this.validTexture,
        ]) {
            if (texture) {
                gl.deleteTexture(texture);
            }
        }
        for (const framebuffer of [
            ...this.stateFramebuffers,
            ...this.trailFramebuffers,
        ]) {
            gl.deleteFramebuffer(framebuffer);
        }
        Object.values(this.programs).forEach((program) =>
            gl.deleteProgram(program),
        );
        this.clearPresentation();
    }

    private buildFieldTextures() {
        const gl = this.gl;
        this.windTextures.forEach((texture) => gl.deleteTexture(texture));
        if (this.projectionTexture) {
            gl.deleteTexture(this.projectionTexture);
        }
        if (this.validTexture) {
            gl.deleteTexture(this.validTexture);
        }
        this.windTextures = [
            this.texture(
                this.field.width,
                this.field.height,
                gl.RG32F,
                gl.RG,
                gl.FLOAT,
                null,
            ),
            this.texture(
                this.field.width,
                this.field.height,
                gl.RG32F,
                gl.RG,
                gl.FLOAT,
                null,
            ),
        ];
        const n = this.field.width * this.field.height;
        const projection = new Float32Array(n);
        const valid = new Uint8Array(n);
        for (let index = 0; index < n; index++) {
            projection[index] = this.field.projection[index] + 640;
            valid[index] = this.field.u[index] === 32767 ? 0 : 255;
        }
        this.projectionTexture = this.texture(
            this.field.width,
            this.field.height,
            gl.R32F,
            gl.RED,
            gl.FLOAT,
            projection,
        );
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
        this.validTexture = this.texture(
            this.field.width,
            this.field.height,
            gl.R8,
            gl.RED,
            gl.UNSIGNED_BYTE,
            valid,
        );
        gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
        this.buildInflowTextures();
    }

    // 境界流入 (ParticleInflow) の線分を RGBA32F、CDF を R32F へ。
    // 幅 1024 の 2D 敷詰め (MSM の線分数 ~2080 が最小保証テクスチャ幅 2048 を超えるため)
    private buildInflowTextures() {
        const gl = this.gl;
        if (this.inflowSegTexture) {
            gl.deleteTexture(this.inflowSegTexture);
        }
        if (this.inflowCdfTexture) {
            gl.deleteTexture(this.inflowCdfTexture);
        }
        this.inflow = buildInflowSegments(this.field, this.geometry);
        this.inflowRate = 0;
        const rows = Math.max(1, Math.ceil(this.inflow.n / 1024));
        const segments = new Float32Array(1024 * rows * 4);
        segments.set(this.inflow.xy);
        this.inflowCdfRows = Math.max(1, Math.ceil((this.inflow.n + 1) / 1024));
        this.inflowCdf = new Float32Array(1024 * this.inflowCdfRows);
        this.inflowSegTexture = this.texture(
            1024,
            rows,
            gl.RGBA32F,
            gl.RGBA,
            gl.FLOAT,
            segments,
        );
        this.inflowCdfTexture = this.texture(
            1024,
            this.inflowCdfRows,
            gl.R32F,
            gl.RED,
            gl.FLOAT,
            this.inflowCdf,
        );
    }

    private updateInflow(ftA: number) {
        if (!this.inflow) {
            return;
        }
        this.inflowRate = computeInflowCdf(
            this.inflow,
            this.field,
            ftA,
            this.geometry,
            this.settings,
            this.inflowCdf,
        );
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.inflowCdfTexture);
        gl.texSubImage2D(
            gl.TEXTURE_2D,
            0,
            0,
            0,
            1024,
            this.inflowCdfRows,
            gl.RED,
            gl.FLOAT,
            this.inflowCdf,
        );
    }

    private buildStateTextures() {
        const gl = this.gl;
        this.stateTextures.forEach((texture) => gl.deleteTexture(texture));
        this.stateFramebuffers.forEach((fb) => gl.deleteFramebuffer(fb));
        if (this.segmentTexture) {
            gl.deleteTexture(this.segmentTexture);
        }
        this.particleN = particleCount(
            this.field,
            this.geometry,
            this.settings.density,
        );
        this.stateSide = Math.ceil(Math.sqrt(this.particleN));
        const initial = new Float32Array(this.stateSide * this.stateSide * 4);
        const random = seededRandom();
        for (let index = 0; index < this.stateSide * this.stateSide; index++) {
            const point = this.randomPoint(random);
            initial[index * 4] = point.x;
            initial[index * 4 + 1] = point.y;
            initial[index * 4 + 2] = point.valid
                ? Math.floor(
                      random() * this.settings.age * PARTICLE_STEPS_PER_FT,
                  )
                : 1e9;
            initial[index * 4 + 3] = random();
        }
        this.stateTextures = [
            this.texture(
                this.stateSide,
                this.stateSide,
                gl.RGBA32F,
                gl.RGBA,
                gl.FLOAT,
                initial,
            ),
            this.texture(
                this.stateSide,
                this.stateSide,
                gl.RGBA32F,
                gl.RGBA,
                gl.FLOAT,
                initial,
            ),
        ];
        this.segmentTexture = this.texture(
            this.stateSide,
            this.stateSide,
            gl.RG32F,
            gl.RG,
            gl.FLOAT,
            null,
        );
        this.stateFramebuffers = [
            gl.createFramebuffer()!,
            gl.createFramebuffer()!,
        ];
        for (let index = 0; index < 2; index++) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.stateFramebuffers[index]);
            gl.framebufferTexture2D(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                gl.TEXTURE_2D,
                this.stateTextures[index],
                0,
            );
            gl.framebufferTexture2D(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT1,
                gl.TEXTURE_2D,
                this.segmentTexture,
                0,
            );
            this.requireComplete("粒子状態");
        }
    }

    private buildTrailTextures() {
        const gl = this.gl;
        this.trailTextures.forEach((texture) => gl.deleteTexture(texture));
        this.trailFramebuffers.forEach((fb) => gl.deleteFramebuffer(fb));
        this.trailTextures = [
            this.texture(
                this.geometry.backWidth,
                this.geometry.backHeight,
                gl.R8,
                gl.RED,
                gl.UNSIGNED_BYTE,
                null,
                gl.LINEAR,
            ),
            this.texture(
                this.geometry.backWidth,
                this.geometry.backHeight,
                gl.R8,
                gl.RED,
                gl.UNSIGNED_BYTE,
                null,
                gl.LINEAR,
            ),
        ];
        this.trailFramebuffers = [
            gl.createFramebuffer()!,
            gl.createFramebuffer()!,
        ];
        for (let index = 0; index < 2; index++) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, this.trailFramebuffers[index]);
            gl.framebufferTexture2D(
                gl.FRAMEBUFFER,
                gl.COLOR_ATTACHMENT0,
                gl.TEXTURE_2D,
                this.trailTextures[index],
                0,
            );
            this.requireComplete("粒子トレイル");
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    private advect(fraction: number) {
        const gl = this.gl;
        const source = this.stateIndex;
        const destination = 1 - source;
        const program = this.programs.advect;
        gl.disable(gl.BLEND);
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.stateFramebuffers[destination]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
        gl.viewport(0, 0, this.stateSide, this.stateSide);
        gl.useProgram(program);
        this.bindTexture(program, 0, this.windTextures[0], "uWindA");
        this.bindTexture(program, 1, this.windTextures[1], "uWindB");
        this.bindTexture(program, 2, this.stateTextures[source], "uState");
        this.bindTexture(program, 3, this.validTexture!, "uValid");
        this.uniform1f(program, "uFraction", fraction);
        this.uniform2f(
            program,
            "uGridSize",
            this.field.width,
            this.field.height,
        );
        this.uniform2f(program, "uOrigin", this.geometry.rx, this.geometry.ry);
        this.uniform2f(
            program,
            "uInvGrid",
            1 / (this.geometry.gridDx * this.geometry.scale),
            1 / (this.geometry.gridDy * this.geometry.scale),
        );
        this.uniform2f(program, "uExtent", this.geometry.rw, this.geometry.rh);
        this.uniform1f(
            program,
            "uSpeed",
            (3600 / PARTICLE_STEPS_PER_FT) *
                this.geometry.scale *
                this.settings.speed,
        );
        this.uniform1f(
            program,
            "uMaxAge",
            this.settings.age * PARTICLE_STEPS_PER_FT,
        );
        this.uniform1f(program, "uFrame", (this.count % 4096) + 0.5);
        this.bindTexture(program, 4, this.inflowSegTexture!, "uInflowSeg");
        this.bindTexture(program, 5, this.inflowCdfTexture!, "uInflowCdf");
        this.uniform1f(program, "uInflowRate", this.inflowRate);
        this.uniform1f(program, "uInflowN", this.inflow ? this.inflow.n : 0);
        gl.bindVertexArray(this.quadVao);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        this.stateIndex = destination;
    }

    private fadeTrail() {
        const gl = this.gl;
        const source = this.trailIndex;
        const destination = 1 - source;
        const program = this.programs.fade;
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.trailFramebuffers[destination]);
        gl.drawBuffers([gl.COLOR_ATTACHMENT0]);
        gl.viewport(0, 0, this.geometry.backWidth, this.geometry.backHeight);
        gl.useProgram(program);
        this.bindTexture(program, 0, this.trailTextures[source], "uPrevious");
        this.uniform1f(
            program,
            "uVanish",
            Math.pow(10, -1 / Math.pow(this.settings.length, 1.5)),
        );
        gl.bindVertexArray(this.quadVao);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        this.trailIndex = destination;
    }

    private drawLines() {
        const gl = this.gl;
        const program = this.programs.line;
        gl.useProgram(program);
        gl.enable(gl.BLEND);
        gl.blendEquation(gl.MAX);
        this.bindTexture(
            program,
            0,
            this.stateTextures[this.stateIndex],
            "uState",
        );
        this.bindTexture(program, 1, this.segmentTexture!, "uSegment");
        this.uniform1f(program, "uSide", this.stateSide);
        this.uniform2f(
            program,
            "uCanvas",
            this.geometry.backWidth,
            this.geometry.backHeight,
        );
        this.uniform1f(
            program,
            "uLineWidth",
            Math.max(this.settings.lineWidth, 0.75),
        );
        gl.bindVertexArray(this.lineVao);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.particleN);
        gl.blendEquation(gl.FUNC_ADD);
        gl.disable(gl.BLEND);
    }

    private uploadWind(slot: number, ft: number) {
        const gl = this.gl;
        const n = this.field.width * this.field.height;
        const offset = Math.min(Math.max(ft, 0), this.field.timeCount - 1) * n;
        const values = new Float32Array(n * 2);
        for (let index = 0; index < n; index++) {
            const u = this.field.u[offset + index];
            const v = this.field.v[offset + index];
            values[index * 2] = u === 32767 ? 0 : u / 100000;
            values[index * 2 + 1] = u === 32767 ? 0 : v / 100000;
        }
        gl.bindTexture(gl.TEXTURE_2D, this.windTextures[slot]);
        gl.texSubImage2D(
            gl.TEXTURE_2D,
            0,
            0,
            0,
            this.field.width,
            this.field.height,
            gl.RG,
            gl.FLOAT,
            values,
        );
    }

    private randomPoint(random: () => number) {
        for (let attempt = 0; attempt < 100; attempt++) {
            const x = this.geometry.rx + random() * this.geometry.rw;
            const y = this.geometry.ry + random() * this.geometry.rh;
            const gx = Math.floor(
                (x - this.geometry.rx) /
                    (this.geometry.gridDx * this.geometry.scale),
            );
            const gy = Math.floor(
                (y - this.geometry.ry) /
                    (this.geometry.gridDy * this.geometry.scale),
            );
            if (this.validCell(gx, gy)) {
                return { x, y, valid: true };
            }
        }
        return { x: this.geometry.rx, y: this.geometry.ry, valid: false };
    }

    private validCell(x: number, y: number) {
        if (
            x < 0 ||
            y < 0 ||
            x >= this.field.width - 1 ||
            y >= this.field.height - 1
        ) {
            return false;
        }
        const offset = y * this.field.width + x;
        return (
            this.field.u[offset] !== 32767 &&
            this.field.u[offset + 1] !== 32767 &&
            this.field.u[offset + this.field.width] !== 32767 &&
            this.field.u[offset + this.field.width + 1] !== 32767
        );
    }

    private viewMatrix() {
        if (this.geometry.degree === 0) {
            return new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);
        }
        const radians = (-this.geometry.degree / 180) * Math.PI;
        const cosine = Math.cos(radians);
        const sine = Math.sin(radians);
        const cx = PARTICLE_WIDTH / 2;
        const cy = PARTICLE_HEIGHT / 2;
        const tx = cx - cosine * cx + sine * cy - this.geometry.rx0;
        const ty = cy - sine * cx - cosine * cy - this.geometry.ry0;
        return new Float32Array([cosine, sine, 0, -sine, cosine, 0, tx, ty, 1]);
    }

    private compilePrograms(): Programs {
        const quadVertex = `#version 300 es
layout(location=0) in vec2 position;
out vec2 uv;
void main(){ uv=position*0.5+0.5; gl_Position=vec4(position,0.,1.); }`;
        const windSample = `
uniform sampler2D uWindA;
uniform sampler2D uWindB;
uniform float uFraction;
uniform vec2 uGridSize;
vec2 windAt(vec2 grid){
  vec2 base=floor(grid); vec2 ratio=grid-base; ivec2 cell=ivec2(base);
  vec2 a00=mix(texelFetch(uWindA,cell,0).rg,texelFetch(uWindB,cell,0).rg,uFraction);
  vec2 a01=mix(texelFetch(uWindA,cell+ivec2(1,0),0).rg,texelFetch(uWindB,cell+ivec2(1,0),0).rg,uFraction);
  vec2 a10=mix(texelFetch(uWindA,cell+ivec2(0,1),0).rg,texelFetch(uWindB,cell+ivec2(0,1),0).rg,uFraction);
  vec2 a11=mix(texelFetch(uWindA,cell+ivec2(1,1),0).rg,texelFetch(uWindB,cell+ivec2(1,1),0).rg,uFraction);
  return mix(mix(a00,a01,ratio.x),mix(a10,a11,ratio.x),ratio.y);
}`;
        const advectFragment = `#version 300 es
precision highp float;
${windSample}
uniform sampler2D uState; uniform sampler2D uValid;
uniform sampler2D uInflowSeg; uniform sampler2D uInflowCdf;
uniform float uInflowRate; uniform float uInflowN;
uniform vec2 uOrigin; uniform vec2 uInvGrid; uniform vec2 uExtent;
uniform float uSpeed; uniform float uMaxAge; uniform float uFrame;
layout(location=0) out vec4 nextState;
layout(location=1) out vec2 segmentStart;
float hash(vec2 value){ value=fract(value*vec2(123.34,456.21)); value+=dot(value,value+45.32); return fract(value.x*value.y); }
bool validCell(vec2 point){
  vec2 grid=(point-uOrigin)*uInvGrid;
  if(grid.x<0.||grid.y<0.||grid.x>=uGridSize.x-1.||grid.y>=uGridSize.y-1.) return false;
  ivec2 cell=ivec2(floor(grid));
  return texelFetch(uValid,cell,0).r>.5 && texelFetch(uValid,cell+ivec2(1,0),0).r>.5
    && texelFetch(uValid,cell+ivec2(0,1),0).r>.5 && texelFetch(uValid,cell+ivec2(1,1),0).r>.5;
}
void main(){
  vec4 state=texelFetch(uState,ivec2(gl_FragCoord.xy),0);
  vec2 point=state.xy; float age=state.z+1.; float seed=state.w;
  if(age>uMaxAge || !validCell(point)){
    float select=hash(vec2(seed*3.19,uFrame+11.3));
    if(select<uInflowRate && uInflowN>.5){
      float r=hash(vec2(seed*271.7,uFrame+6.9));
      int lo=0; int hi=int(uInflowN)-1;
      for(int iteration=0;iteration<12;iteration++){
        if(lo>=hi) break;
        int mid=(lo+hi)/2;
        if(texelFetch(uInflowCdf,ivec2((mid+1)&1023,(mid+1)>>10),0).r<r) lo=mid+1; else hi=mid;
      }
      vec4 segment=texelFetch(uInflowSeg,ivec2(lo&1023,lo>>10),0);
      point=segment.xy+segment.zw*hash(vec2(seed,uFrame+2.3));
    }else{
      for(int attempt=0;attempt<4;attempt++){
        seed=hash(vec2(seed*7.13+float(attempt),uFrame));
        vec2 candidate=uOrigin+vec2(seed,hash(vec2(seed,uFrame+3.7)))*uExtent;
        if(validCell(candidate)){ point=candidate; break; }
      }
    }
    age=0.;
  }
  vec2 grid=(point-uOrigin)*uInvGrid;
  vec2 wind=windAt(grid);
  vec2 moved=point+vec2(wind.x,-wind.y)*uSpeed;
  nextState=vec4(moved,age,seed); segmentStart=point;
}`;
        const fadeFragment = `#version 300 es
precision highp float;
in vec2 uv; uniform sampler2D uPrevious; uniform float uVanish; out vec4 color;
void main(){ color=vec4(texture(uPrevious,uv).r*uVanish,0.,0.,1.); }`;
        const lineVertex = `#version 300 es
layout(location=0) in vec2 corner;
uniform sampler2D uState; uniform sampler2D uSegment;
uniform float uSide; uniform vec2 uCanvas; uniform float uLineWidth;
void main(){
  int id=gl_InstanceID; ivec2 texel=ivec2(id%int(uSide),id/int(uSide));
  vec2 start=texelFetch(uSegment,texel,0).rg; vec2 end=texelFetch(uState,texel,0).rg;
  vec2 delta=end-start; float lengthValue=length(delta);
  vec2 direction=lengthValue>.0001 ? delta/lengthValue : vec2(1.,0.);
  vec2 normal=vec2(-direction.y,direction.x);
  vec2 pixel=start+direction*(corner.x*max(lengthValue,.75))+normal*(corner.y*uLineWidth);
  vec2 ndc=vec2(pixel.x/uCanvas.x*2.-1.,1.-pixel.y/uCanvas.y*2.);
  gl_Position=vec4(ndc,0.,1.);
}`;
        const lineFragment = `#version 300 es
precision highp float; out vec4 color; void main(){ color=vec4(1.,0.,0.,1.); }`;
        const compositeFragment = `#version 300 es
precision highp float;
${windSample}
in vec2 uv; out vec4 color;
uniform sampler2D uTrail; uniform sampler2D uProjection;
uniform vec2 uScreen; uniform vec2 uBack; uniform vec2 uOrigin; uniform vec2 uInvGrid;
uniform float uThresholds[6]; uniform vec3 uColors[7]; uniform mat3 uView;
void main(){
  vec2 screen=vec2(uv.x*uScreen.x,(1.-uv.y)*uScreen.y);
  vec2 back=(uView*vec3(screen,1.)).xy;
  if(back.x<0.||back.y<0.||back.x>=uBack.x||back.y>=uBack.y){ color=vec4(0.); return; }
  float alpha=texture(uTrail,vec2(back.x/uBack.x,1.-back.y/uBack.y)).r;
  alpha=clamp(1.5*pow(alpha,.5)-.5,0.,1.);
  if(alpha<=0.){ color=vec4(0.); return; }
  vec2 grid=clamp((back-uOrigin)*uInvGrid,vec2(0.),uGridSize-vec2(1.001));
  vec2 wind=windAt(grid); vec2 base=floor(grid); vec2 ratio=grid-base; ivec2 cell=ivec2(base);
  float projection=mix(mix(texelFetch(uProjection,cell,0).r,texelFetch(uProjection,cell+ivec2(1,0),0).r,ratio.x),
    mix(texelFetch(uProjection,cell+ivec2(0,1),0).r,texelFetch(uProjection,cell+ivec2(1,1),0).r,ratio.x),ratio.y);
  float speed2=dot(wind*projection,wind*projection); int level=0;
  for(int index=0;index<6;index++){ if(speed2>=uThresholds[index]) level=index+1; }
  vec3 rgb=uColors[level]/255.; color=vec4(rgb*alpha,alpha);
}`;
        return {
            advect: this.program(quadVertex, advectFragment),
            fade: this.program(quadVertex, fadeFragment),
            line: this.program(lineVertex, lineFragment),
            composite: this.program(quadVertex, compositeFragment),
        };
    }

    private createVaos(): [WebGLVertexArrayObject, WebGLVertexArrayObject] {
        const gl = this.gl;
        const make = (values: number[]) => {
            const vao = gl.createVertexArray()!;
            const buffer = gl.createBuffer()!;
            gl.bindVertexArray(vao);
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
            gl.bufferData(
                gl.ARRAY_BUFFER,
                new Float32Array(values),
                gl.STATIC_DRAW,
            );
            gl.enableVertexAttribArray(0);
            gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
            return vao;
        };
        return [
            make([-1, -1, 1, -1, -1, 1, 1, 1]),
            make([0, -0.5, 0, 0.5, 1, -0.5, 1, 0.5]),
        ];
    }

    private shader(type: number, source: string) {
        const shader = this.gl.createShader(type)!;
        this.gl.shaderSource(shader, source);
        this.gl.compileShader(shader);
        if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
            throw new Error(
                this.gl.getShaderInfoLog(shader) || "shader compile error",
            );
        }
        return shader;
    }

    private program(vertex: string, fragment: string) {
        const program = this.gl.createProgram()!;
        const vertexShader = this.shader(this.gl.VERTEX_SHADER, vertex);
        const fragmentShader = this.shader(this.gl.FRAGMENT_SHADER, fragment);
        this.gl.attachShader(program, vertexShader);
        this.gl.attachShader(program, fragmentShader);
        this.gl.linkProgram(program);
        this.gl.deleteShader(vertexShader);
        this.gl.deleteShader(fragmentShader);
        if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
            throw new Error(
                this.gl.getProgramInfoLog(program) || "program link error",
            );
        }
        return program;
    }

    private texture(
        width: number,
        height: number,
        internalFormat: number,
        format: number,
        type: number,
        data: ArrayBufferView | null,
        filter: number = this.gl.NEAREST,
    ) {
        const gl = this.gl;
        const texture = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            internalFormat,
            width,
            height,
            0,
            format,
            type,
            data,
        );
        return texture;
    }

    private bindTexture(
        program: WebGLProgram,
        unit: number,
        texture: WebGLTexture,
        name: string,
    ) {
        this.gl.activeTexture(this.gl.TEXTURE0 + unit);
        this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
        this.gl.uniform1i(this.gl.getUniformLocation(program, name), unit);
    }

    private uniform1f(program: WebGLProgram, name: string, value: number) {
        this.gl.uniform1f(this.gl.getUniformLocation(program, name), value);
    }

    private uniform2f(
        program: WebGLProgram,
        name: string,
        x: number,
        y: number,
    ) {
        this.gl.uniform2f(this.gl.getUniformLocation(program, name), x, y);
    }

    private requireComplete(kind: string) {
        if (
            this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER) !==
            this.gl.FRAMEBUFFER_COMPLETE
        ) {
            throw new Error(`${kind} framebufferを作成できません。`);
        }
    }

    private clearPresentation() {
        this.presentation
            .getContext("2d")
            ?.clearRect(0, 0, PARTICLE_WIDTH, PARTICLE_HEIGHT);
    }
}
